<template>
  <InfuseModal :title="t('e_connector.centralSystems.modal.update.title')" @close="emit('close')">
    <form ref="form" id="update-central-system" @submit.prevent="onSave()">
      <div class="flex max-h-[475px] max-w-[965px] flex-col gap-4 overflow-y-auto px-5">
        <div class="flex items-start gap-4">
          <InfuseField :label="t('e_connector.common.centralSystem.name')" class="basis-full" size="small">
            <InfuseInput
              size="small"
              v-model="centralSystemData.name"
              :maxlength="255"
              :placeholder="t('e_connector.centralSystems.modal.field.name.placeholder')"
              :required="true"
            />
          </InfuseField>
        </div>

        <div class="flex items-start gap-4">
          <InfuseField :label="t('e_connector.common.centralSystem.url')" class="basis-full" size="small">
            <InfuseInput
              size="small"
              v-model="centralSystemData.uri"
              :maxlength="255"
              :placeholder="t('e_connector.centralSystems.modal.field.uri.placeholder')"
              :required="true"
              type="url"
            />
          </InfuseField>
        </div>

        <div v-if="!ocpp16WebsocketOnly" class="flex items-start gap-4">
          <InfuseField :label="t('e_connector.common.centralSystem.type')" class="basis-1/2" size="small">
            <InfuseSelect
              name="type"
              size="small"
              v-model="centralSystemData.type"
              :options="typeOptions"
              :label="t('e_connector.centralSystems.modal.field.type.placeholder')"
              :required="true"
            />
          </InfuseField>

          <InfuseField :label="t('e_connector.common.centralSystem.ocppVersion')" class="basis-1/2" size="small">
            <InfuseSelect
              name="ocppVersion"
              size="small"
              v-model="centralSystemData.ocppVersion"
              :options="ocppVersionOptions"
              :label="t('e_connector.centralSystems.modal.field.ocppVersion.placeholder')"
              :required="true"
            />
          </InfuseField>
        </div>

        <div v-if="isDomainUser" class="flex items-start gap-4">
          <InfuseField :label="t('common.visibility.label')" class="basis-1/2" size="small">
            <InfuseSelect
              name="visibility"
              size="small"
              v-model="centralSystemData.visibility"
              :options="visibilityOptions"
              :format="formatVisibilityLabel"
              :label="t('common.visibility.option.private.label')"
            />
          </InfuseField>
        </div>
      </div>
    </form>

    <template #actions>
      <InfuseButton data-testid="modal-cancel-button" color="secondary" @click="emit('close')">
        {{ t('e_connector.common.action.cancel') }}
      </InfuseButton>
      <InfuseButton
        data-testid="modal-confirm-button"
        type="submit"
        form="update-central-system"
        :disabled="loading"
        :loading="loading"
      >
        {{ t('e_connector.common.action.update') }}
      </InfuseButton>
    </template>
  </InfuseModal>
</template>

<script setup lang="ts">
import { InfuseButton, InfuseField, InfuseInput, InfuseModal, InfuseSelect } from '@infuse/ui/components';
import { inject, Ref, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { CentralSystem, ConnectionType, OcppVersion } from '@infuse/e-connector/central-system/domain';
import { AbacVisibility } from '@infuse/api/common';
import { useCurrentUserLevel } from '@infuse/iam/composables';
import { ConnectorConfig } from '@infuse/e-connector/config/plugin';

interface Props {
  loading: boolean;
  centralSystem: CentralSystem;
}

interface Emits {
  (e: 'close'): void;
  (e: 'update', centralSystem: CentralSystem): void;
}

const { t } = useI18n();

const props = withDefaults(defineProps<Props>(), {
  loading: false
});

const emit = defineEmits<Emits>();

const { isDomainUser } = useCurrentUserLevel();

// Hidden, not forced: existing records keep their stored type and version on save.
const ocpp16WebsocketOnly = import.meta.env.VITE_UI_VARIANT_BACKOFFICE_OCPP16_WEBSOCKET_ONLY === 'true';

const config = inject<Ref<ConnectorConfig>>('connectorConfig')?.value;
const typeOptions = Object.values(ConnectionType).filter((v) => {
  switch (v) {
    case ConnectionType.AWS_IOT:
      return !!config?.isAwsIotAvailable;
    case ConnectionType.AWS_SNS:
      return !!config?.isAwsSnsAvailable;
    case ConnectionType.JAVA_API:
      return false;
    default:
      return true;
  }
});
const ocppVersionOptions = Object.values(OcppVersion);
const visibilityOptions: { key: string; value: AbacVisibility }[] = [
  { key: 'PRIVATE', value: AbacVisibility.PRIVATE },
  { key: 'GLOBAL', value: AbacVisibility.GLOBAL }
];

const centralSystemData = ref<CentralSystem>({ ...props.centralSystem }); // copy

function formatVisibilityLabel(key: string): string {
  return t(`common.visibility.option.${key.toLowerCase()}.label`);
}

function onSave() {
  emit('update', centralSystemData.value);
}
</script>
